<script setup>
// NOTICE: KAPTION-INTEGRATION
import { computed, onMounted, nextTick, ref } from 'vue';
import { useSidebarContext } from './provider';
import { useRoute, useRouter } from 'vue-router';
import Policy from 'dashboard/components/policy.vue';
import KaptionSidebarGroupHeaderV2 from './KaptionSidebarGroupHeaderV2.vue';
import KaptionSidebarDropdownV2 from './KaptionSidebarDropdownV2.vue';
import { useDropdownManager } from './useDropdownManager.js';

const props = defineProps({
  name: { type: String, required: true },
  label: { type: String, required: true },
  icon: { type: [String, Object, Function], default: null },
  to: { type: Object, default: null },
  activeOn: { type: Array, default: () => [] },
  children: { type: Array, default: undefined },
  getterKeys: { type: Object, default: () => ({}) },
});

const { resolvePath, resolvePermissions, resolveFeatureFlag, isAllowed } =
  useSidebarContext();

const navigableChildren = computed(() => {
  return props.children?.flatMap(child => child.children || child) || [];
});

const route = useRoute();
const router = useRouter();
const hasChildren = computed(
  () => Array.isArray(props.children) && props.children.length > 0
);

const accessibleItems = computed(() => {
  if (!hasChildren.value) return [];
  return props.children.filter(child => {
    // If a item has no link, it means it's just a subgroup header
    // So we don't need to check for permissions here, because there's nothing to
    // access here anyway
    return child.to && isAllowed(child.to);
  });
});

const hasAccessibleChildren = computed(() => {
  return accessibleItems.value.length > 0;
});

const isActive = computed(() => {
  if (props.to) {
    if (route.path === resolvePath(props.to)) return true;

    return props.activeOn.includes(route.name);
  }

  return false;
});

// We could use the RouterLink isActive too, but our routes are not always
// nested correctly, so we need to check the active state ourselves
// TODO: Audit the routes and fix the nesting and remove this
// NOTICE: KAPTION-INTEGRATION - Enhanced to handle query param-based navigation
const activeChild = computed(() => {
  // Helper to check if child has specific query params that match current route
  const hasMatchingQueryParams = child => {
    const childQuery = child.to?.query || {};
    const hasChildQuery = Object.keys(childQuery).length > 0;
    if (!hasChildQuery) return false; // No specific query params

    return Object.entries(childQuery).every(
      ([key, value]) => route.query[key] === value
    );
  };

  // First, check for exact path AND specific query param match (prioritize items with query params)
  const exactMatchWithQuery = navigableChildren.value.find(child => {
    if (!child.to) return false;
    const childPath = resolvePath(child.to);
    if (route.path !== childPath) return false;
    return hasMatchingQueryParams(child);
  });
  if (exactMatchWithQuery) return exactMatchWithQuery;

  // Then, check for path match with no query params (only if route also has no relevant query params)
  const routeHasAssigneeQuery = !!route.query.assignee;
  const exactMatchNoQuery = navigableChildren.value.find(child => {
    if (!child.to) return false;
    const childPath = resolvePath(child.to);
    if (route.path !== childPath) return false;
    const childQuery = child.to?.query || {};
    const hasChildQuery = Object.keys(childQuery).length > 0;
    // Only match items with no query if route also has no assignee query
    return !hasChildQuery && !routeHasAssigneeQuery;
  });
  if (exactMatchNoQuery) return exactMatchNoQuery;

  // Rank the activeOn Prop higher than the path match
  // There will be cases where the path name is the same but the params are different
  // So we need to rank them based on the params
  // For example, contacts segment list in the sidebar effectively has the same name
  // But the params are different
  const activeOnPages = navigableChildren.value.filter(child =>
    child.activeOn?.includes(route.name)
  );

  if (activeOnPages.length > 0) {
    const rankedPage = activeOnPages.find(child => {
      return Object.keys(child.to.params)
        .map(key => {
          return String(child.to.params[key]) === String(route.params[key]);
        })
        .every(match => match);
    });

    // If there is no ranked page, return the first activeOn page anyway
    // Since this takes higher precedence over the path match
    // This is not perfect, ideally we should rank each route based on all the techniques
    // and then return the highest ranked one
    // But this is good enough for now
    return rankedPage ?? activeOnPages[0];
  }

  return navigableChildren.value.find(
    child => child.to && route.path.startsWith(resolvePath(child.to))
  );
});

const hasActiveChild = computed(() => {
  return activeChild.value !== undefined;
});

const showDropdown = ref(false);
const headerRef = ref(null);
const { openDropdown, closeDropdown } = useDropdownManager();

const toggleTrigger = () => {
  // If has children, toggle dropdown
  if (hasChildren.value && hasAccessibleChildren.value) {
    if (showDropdown.value) {
      closeDropdown(props.name);
      showDropdown.value = false;
    } else {
      // First close any other dropdowns, then open this one
      openDropdown(props.name);
      // Use nextTick to ensure the close event is processed first
      nextTick(() => {
        showDropdown.value = true;
      });
    }
    return;
  }

  // Otherwise, navigate if it has a direct link
  if (props.to) {
    router.push(props.to);
  }
};

const handleCloseDropdown = () => {
  closeDropdown(props.name);
  showDropdown.value = false;
};

onMounted(async () => {
  await nextTick();

  // Listen for close events from other dropdowns
  const handleCloseEvent = event => {
    if (event.detail.id === props.name) {
      showDropdown.value = false;
    }
  };

  document.addEventListener('close-dropdown', handleCloseEvent);

  // Cleanup listener on unmount
  return () => {
    document.removeEventListener('close-dropdown', handleCloseEvent);
  };
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    v-if="!hasChildren || hasAccessibleChildren"
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="text-sm cursor-pointer select-none relative"
  >
    <KaptionSidebarGroupHeaderV2
      ref="headerRef"
      :icon
      :name
      :label
      :to
      :getter-keys="getterKeys"
      :is-active="isActive || showDropdown"
      :has-active-child="hasActiveChild"
      :expandable="hasChildren"
      :is-expanded="showDropdown"
      @toggle="toggleTrigger"
    />
    <KaptionSidebarDropdownV2
      v-if="showDropdown && hasChildren"
      :children="children"
      :label="label"
      :trigger-element="headerRef?.$el"
      @close="handleCloseDropdown"
    />
  </Policy>
</template>
